import { defineStore } from 'pinia'
import state from './state'
import getters from './getters'
import usePaginationStore from '../pagination'
import SpreadingJobsApiRequest from '@/api/spreadingJob'
import { SpreadingJob, SpreadingJobForm, SpreadingJobUpdateForm } from '@/models'
import StatusCodeType from '@/enum/StatusCodeType'
import CONSTANTS from '@/utils/constants/string-constants'
import ErrorHandlerService from '@/services/ErrorHandlerService'
import { markRaw } from 'vue'
import useGlobalStore from '../global'
import {
  DEFAULT_PAGE_OFFSET,
  MESSAGE_CONSTANTS,
  Network,
  SPREADING_JOBS_DETAILS_URL_NAME,
  SPREADING_JOBS_EDIT_JOB_URL_NAME,
  SPREADING_JOBS_LIST_STORE
} from '@/utils/constants'
import {
  CustomFilterTypeExpression,
  DeleteTypeExpression,
  ItemTypeExpression,
  ListFilterTypeExpression,
  ListPagingTypeExpression,
  ListResponseTypeExpression
} from '@/enum/LinqExpressionType'
import { fetchUntilExhaustion, pluralize } from '@/utils/helpers'
import NotificationType, { NotificationDuration } from '@/enum/NotificationType'
import type { TApiResponse } from '@/types/api-types/TApiResponse'
import type { ISpreadingJob, ISpreadingJobForm, ISpreadingJobUpdateForm } from '@/interfaces'
import moment from 'moment'
import ChangeStatus from '@/enum/ChangeStatus'
import { ExpandResponseType } from '@/enum'

const useSpreadingJobsStore = defineStore('spreadingJobsStore', {
  state: () => state,
  getters,
  actions: {
    async getSpreadingJob(
      storeName: string,
      responseOptions?: Record<string, string | Record<string, string | string[]>>
    ) {
      const listStore = usePaginationStore(storeName)
      const spreadingJobApi = new SpreadingJobsApiRequest()
      responseOptions = {
        ...responseOptions,
        [ListResponseTypeExpression.Expand]: `${ExpandResponseType.FARM},${ExpandResponseType.ASSIGNED_EMPLOYEE},${ExpandResponseType.ASSIGNED_VEHICLE},${ExpandResponseType.FIELD},${ExpandResponseType.SILO},
          ${ExpandResponseType.WIND_SPEED},${ExpandResponseType.TEMPERATURE}`
      }
      listStore.handleListAPICall<SpreadingJob>(
        spreadingJobApi.getSpreadingJob.bind(spreadingJobApi),
        SpreadingJob,
        responseOptions
      )
    },

    async getJobsForCalendar(start: string, end: string, options?: any) {
      const spreadingJobApi = new SpreadingJobsApiRequest()
      const {
        [ListFilterTypeExpression.SearchText]: searchText,
        [ListFilterTypeExpression.Filter]: filterOptions
      } = options ?? {}
      const startDate = moment(start).format('YYYY-MM-DD')
      const endDate = moment(end).format('YYYY-MM-DD')
      let responseOptions: any = {
        [ListResponseTypeExpression.Expand]: `${ExpandResponseType.ASSIGNED_EMPLOYEE},${ExpandResponseType.FARM}`,
        [ListFilterTypeExpression.Filter]: {
          ...filterOptions,
          [CustomFilterTypeExpression.Expression]: `ScheduleDate>="${startDate}"&&ScheduleDate<="${endDate}"`
        }
      }
      if (searchText) {
        responseOptions = {
          ...responseOptions,
          [ListFilterTypeExpression.SearchText]: searchText
        }
      }
      const result = await fetchUntilExhaustion(
        spreadingJobApi.getSpreadingJob.bind(spreadingJobApi),
        responseOptions
      )
      if (result.length) {
        const resData = result.map((job) => job).filter((job) => job.scheduleDate)
        return resData
      }
    },

    async getJobDetails(id: string) {
      this.isSpreadingFormLoading = true
      try {
        const spreadingJobApi = new SpreadingJobsApiRequest()
        const result = await spreadingJobApi.getSpreadingJobById({
          id,
          [ItemTypeExpression.Expand]: `${ExpandResponseType.FARM},${ExpandResponseType.ASSIGNED_EMPLOYEE},${ExpandResponseType.ASSIGNED_VEHICLE},${ExpandResponseType.FIELD},${ExpandResponseType.SILO},
          ${ExpandResponseType.WIND_SPEED},${ExpandResponseType.TEMPERATURE}`
        })
        if (result.status === StatusCodeType.Success && result.data) {
          this.spreadingJobDetails = new SpreadingJob(result.data)
        }
      } catch (error: any) {
        ErrorHandlerService.handleError(error, error)
      } finally {
        this.isSpreadingFormLoading = false
      }
    },

    async proccedToUpdateJobStatus(id: string, status?: number, currentStatus?: number, statusUpdate?: boolean) {
      if (id) this.isSpreadingFormButtonLoading = true
      if (status != undefined) {
        this.spreadingJobDetails.status = status
      }
      const result = await this.updateSpreadingJobs(id, statusUpdate)
      if (result.status === StatusCodeType.Success && result.data) {
        this.spreadingJobDetails = new SpreadingJob(result.data)
      } else {
        if (currentStatus != undefined) {
          this.spreadingJobDetails.status = currentStatus
        }
      }
      this.isSpreadingFormButtonLoading = false
      return result
    },

    resetSpreadingJobForm() {
      this.jobForm = new SpreadingJobForm()
    },

    async createSpreadingJobs(): Promise<{ status: StatusCodeType, data?: ISpreadingJob }> {
      const spreadingJobApi = new SpreadingJobsApiRequest()
      const spreadingForm = markRaw(new SpreadingJobForm(this.jobForm))
      const globalStore = useGlobalStore()
      const result = await spreadingJobApi.addSpreadingJob(spreadingForm, {
        [ItemTypeExpression.Expand]: `${ExpandResponseType.FARM},${ExpandResponseType.ASSIGNED_EMPLOYEE},${ExpandResponseType.ASSIGNED_VEHICLE},${ExpandResponseType.FIELD},${ExpandResponseType.SILO},
        ${ExpandResponseType.WIND_SPEED},${ExpandResponseType.TEMPERATURE}`
      })
      if (result.status === StatusCodeType.Success) {
        globalStore.addToastNotification({
          id: Symbol(`Spreading job`),
          message: `Spreading job ${CONSTANTS.CREATED_SUCCESSFULLY}`,
          type: NotificationType.Success
        })
      }
      return result
    },

    async updateSpreadingJobs(id: string, isStatusUpdate?: boolean): TApiResponse {
      const spreadingJobApi = new SpreadingJobsApiRequest()
      const spreadingForm = markRaw(
        new SpreadingJobUpdateForm(this.spreadingJobDetails as ISpreadingJobUpdateForm)
      )
      const globalStore = useGlobalStore()
      const result = await spreadingJobApi.editSpreadingJob(
        {
          id,
          [ItemTypeExpression.Expand]: `${ExpandResponseType.FARM},${ExpandResponseType.ASSIGNED_EMPLOYEE},${ExpandResponseType.ASSIGNED_VEHICLE},${ExpandResponseType.FIELD},
          ${ExpandResponseType.SILO},${ExpandResponseType.WIND_SPEED},${ExpandResponseType.TEMPERATURE}`
        },
        spreadingForm
      )
      if (result.status === StatusCodeType.Success) {
        globalStore.addToastNotification({
          id: Symbol(`Spreading job`),
          message: `Spreading job ${isStatusUpdate ? CONSTANTS.STATUS_UPDATED_SUCCESSFULLY : CONSTANTS.UPDATED_SUCCESSFULLY}`,
          type: NotificationType.Success
        })
      } else {
        globalStore.addToastNotification({
          id: Symbol(`Spreading job`),
          message: `Spreading job ${MESSAGE_CONSTANTS.FAILED_TO_UPDATE}`,
          type: NotificationType.Failed
        })
      }
      return result
    },

    async deleteSpreadingJob(id?: string) {
      const spreadingJobApi = new SpreadingJobsApiRequest()
      const globalStore = useGlobalStore()
      const spreadingListStore = usePaginationStore(SPREADING_JOBS_LIST_STORE)
      try {
        const templatesToDeleteIds = id ? [id] : (spreadingListStore.selectedItems ?? [])
        if (!templatesToDeleteIds.length) throw new Error(MESSAGE_CONSTANTS.JOBS_UNAVAILABLE)
        let queryParams
        if (
          id ||
          spreadingListStore.someItemsSelected ||
          (spreadingListStore.allItemsSelected && !spreadingListStore.isSelectedAllSnackbarItems)
        ) {
          queryParams = { [DeleteTypeExpression.Expression]: { Id: templatesToDeleteIds } }
        }
        const response = await spreadingListStore.handleListDeleteAPICall(
          spreadingJobApi.deleteSpreadingJob.bind(spreadingJobApi),
          queryParams
        )
        if (
          response?.status === StatusCodeType.Success &&
          response.data.succeeded > 0 &&
          response.data.failed === 0
        ) {
          globalStore.addToastNotification({
            id: Symbol(`Job deleted`),
            message: `${pluralize(response.data.succeeded, 'Spreading job')} ${CONSTANTS.DELETED_SUCCESSFULLY}`,
            type: NotificationType.Success
          })
          return Promise.resolve(response.data)
        }
        if (response?.data && response.data.failed > 0) {
          globalStore.handleBulkOperationErrorsView(response, 'Spreading job')
          return Promise.resolve(response.data)
        }
        if (response?.data?.results.length) return Promise.reject(response.data)
        throw new TypeError(Network.TypeError.BulkOperationTypeMismatch)
      } catch (error: any) {
        ErrorHandlerService.handleAndCatchError(error, error)
      }
      return Promise.reject()
    },

    async initializeJobDetailsForm(jobData: any, navigator?: any) {
      const currentRouteName = navigator?.getCurrentRoute()?.name

      if (!navigator || currentRouteName == SPREADING_JOBS_DETAILS_URL_NAME) {
        this.spreadingJobDetails = new SpreadingJob(jobData)
      }
      if (navigator && currentRouteName == SPREADING_JOBS_EDIT_JOB_URL_NAME) {
        this.getJobDetails(jobData.id)
      }
    },

    async handleRealtimeJobUpdateOnDetails(jobData: any, navigator: any) {
      const loggedInUserId = localStorage.getItem('userId')
      const currentRouteName = navigator.getCurrentRoute()?.name
      const isUserOnJobDetailsView =
        currentRouteName == SPREADING_JOBS_DETAILS_URL_NAME &&
        jobData.id == navigator.getCurrentRoute()?.params?.id
      const isUserOnJobEditView =
        currentRouteName == SPREADING_JOBS_EDIT_JOB_URL_NAME &&
        jobData.id == navigator.getCurrentRoute()?.params?.id

      const hasJobDetailsStatusChanged = this.spreadingJobDetails.status !== jobData.status
      const hasJobEditDetailsStatusChanged = this.spreadingJobDetails.status !== jobData.status
      const hasLoggedInUserUpdatedTheJob = loggedInUserId == jobData.updaterId
      const globalStore = useGlobalStore()
      if (
        !hasLoggedInUserUpdatedTheJob &&
        jobData.changeStatus == ChangeStatus.Deleted &&
        (isUserOnJobDetailsView || isUserOnJobEditView)
      ) {
        globalStore.disableButtonsForRealtime = true
        globalStore.isJobDeletedResponse = true
        return
      }
      // handle job update on job details view
      if (isUserOnJobDetailsView) {
        if (hasJobDetailsStatusChanged && hasLoggedInUserUpdatedTheJob) {
          return
        }

        if (hasJobDetailsStatusChanged && !hasLoggedInUserUpdatedTheJob) {
          this.showJobStatusChangeUpdateNotification(jobData, navigator)
        }

        if (!hasJobDetailsStatusChanged && !hasLoggedInUserUpdatedTheJob) {
          this.showJobDetailsChangeUpdateNotification(jobData, navigator)
        }
      }

      // handle job update on job edit view
      if (isUserOnJobEditView) {
        if (hasJobEditDetailsStatusChanged && hasLoggedInUserUpdatedTheJob) {
          return
        }

        if (hasJobEditDetailsStatusChanged && !hasLoggedInUserUpdatedTheJob) {
          this.showJobStatusChangeUpdateNotification(jobData, navigator)
        }

        if (!hasLoggedInUserUpdatedTheJob) {
          this.showJobDetailsChangeOnEditView()
        }
      }
    },

    async showJobStatusChangeUpdateNotification(updatedData: any, navigator: any) {
      const globalStore = useGlobalStore()
      globalStore.disableButtonsForRealtime = true
      globalStore.addToastNotification({
        id: Symbol(MESSAGE_CONSTANTS.JOB_STATUS_CHANGED),
        message: MESSAGE_CONSTANTS.JOB_STATUS_CHANGED,
        type: NotificationType.Info,
        timeout: NotificationDuration.Infinite,
        callback: () => {
          globalStore.removeAllNotifications()
          this.initializeJobDetailsForm(updatedData, navigator)
        }
      })
    },

    async showJobDetailsChangeUpdateNotification(updatedData: any, navigator: any) {
      const globalStore = useGlobalStore()
      globalStore.disableButtonsForRealtime = true
      globalStore.addToastNotification({
        id: Symbol(MESSAGE_CONSTANTS.JOB_UPDATED),
        message: MESSAGE_CONSTANTS.JOB_UPDATED,
        type: NotificationType.Info,
        timeout: NotificationDuration.Infinite,
        callback: () => {
          globalStore.removeAllNotifications()
          this.initializeJobDetailsForm(updatedData, navigator)
        }
      })
    },

    async showJobDetailsChangeOnEditView() {
      const globalStore = useGlobalStore()
      globalStore.disableButtonsForRealtime = true
      globalStore.addToastNotification({
        id: Symbol(MESSAGE_CONSTANTS.JOB_EDIT_UPDATED),
        message: MESSAGE_CONSTANTS.JOB_EDIT_UPDATED,
        type: NotificationType.Info,
        timeout: NotificationDuration.Medium
      })
    },

    async handleRealtimeJobUpdateOnList(job: any) {
      const loggedInUserId = localStorage.getItem('userId')
      const hasLoggedInUserUpdatedTheJob = loggedInUserId == job.updaterId
      if (hasLoggedInUserUpdatedTheJob) return

      const isNewlyAddedJob = job.updatedAt === job.createdAt
      const isUpdatedJob = !isNewlyAddedJob

      const jobsListStore = usePaginationStore(SPREADING_JOBS_LIST_STORE)
      const jobIndex = jobsListStore.data?.findIndex((item) => item.id === job.id)
      const hasNoFiltersApplied =
        jobsListStore.offset === DEFAULT_PAGE_OFFSET &&
        Object.entries(jobsListStore.filtersOptions)?.length === 0

      const isJobMatchingFilters = async () => {
        const spreadingJobApi = new SpreadingJobsApiRequest()
        if (jobsListStore.filtersOptions) {
          const lastOffset = jobsListStore.offset - jobsListStore.currentPageSize
          const lastLimit = jobsListStore.limit
          const responseOptions = jobsListStore.filtersOptions
          const res = await spreadingJobApi.getSpreadingJob({
            ...responseOptions,
            [ListPagingTypeExpression.Offset]: lastOffset,
            [ListPagingTypeExpression.Limit]: lastLimit
          })
          if (res.data.value && res.data.value.length > 0) {
            const jobExists = res.data.value.some((item: ISpreadingJob) => item.id === job.id)
            if (jobExists) {
              return true
            }
          }
          return false
        }
        return true
      }

      if (isNewlyAddedJob) {
        if (hasNoFiltersApplied || (await isJobMatchingFilters())) {
          if (jobsListStore.data != null && jobsListStore.data.length != 0) {
            jobsListStore.data?.unshift(new SpreadingJob(job))
          } else {
            this.getSpreadingJob(SPREADING_JOBS_LIST_STORE)
          }
        }
        return
      }

      if (!isUpdatedJob || !jobsListStore.data || jobIndex === undefined) {
        return
      }

      const shouldRemoveJob =
        job.changeStatus === ChangeStatus.Deleted ||
        (Object.entries(jobsListStore.filtersOptions).length > 0 && !(await isJobMatchingFilters()))

      const shouldAddJob =
        Object.entries(jobsListStore.filtersOptions).length > 0 &&
        (await isJobMatchingFilters()) &&
        jobIndex === -1

      const removeJobNotMatchingFilters = async () => {
        if (jobsListStore.data && jobIndex !== undefined && jobIndex !== -1) {
          jobsListStore.data.splice(jobIndex, 1)
          if (jobsListStore.data.length == 0) {
            jobsListStore.resetPaginationStateAndNotFilters()
            this.getSpreadingJob(SPREADING_JOBS_LIST_STORE)
          }
        }
      }

      if (shouldRemoveJob) {
        await removeJobNotMatchingFilters()
        return
      }

      if (shouldAddJob) {
        jobsListStore.data?.unshift(new SpreadingJob(job))
        return
      }
      if (jobIndex == -1) return
      jobsListStore.data[jobIndex] = new SpreadingJob(job)
    }
  }
})
export default useSpreadingJobsStore
